import { PencilSimpleIcon, TrashSimpleIcon } from '@phosphor-icons/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'
import invariant from 'tiny-invariant'
import { match } from 'ts-pattern'
import { Breadcrumb } from '../../components/breadcrumb.tsx'
import { DefaultButton } from '../../components/buttons/default-button.tsx'
import { AddRepositoriesDialog } from '../../components/projects/add-repositories-dialog.tsx'
import { ADMIN_REQUIRED_OVERRIDE } from '../../components/projects/admin-required-override.ts'
import { DUPLICATE_PROJECT_PROBLEM_TYPE } from '../../components/projects/create-project-dialog.tsx'
import { DeleteProjectDialog } from '../../components/projects/delete-project-dialog.tsx'
import { MoveRepositoryDialog } from '../../components/projects/move-repository-dialog.tsx'
import * as layout from '../../components/projects/project-page-layout.css.ts'
import { ProjectRepositoryCard } from '../../components/projects/project-repository-card.tsx'
import * as cardStyles from '../../components/projects/project-row-card.css.ts'
import { RemoveRepositoryDialog } from '../../components/projects/remove-repository-dialog.tsx'
import { TooltipWithHover } from '../../components/tooltip.tsx'
import { useRepositoryRiskScores } from '../../hooks/use-repository-risk-scores.ts'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { Routes } from '../../routes.ts'
import { useToastService } from '../../services/toast-service.tsx'
import {
  useCurrentUser,
  useGetLatestAnalysisForRepository,
  useGetProject,
  useGetRepositories,
  useIsAdmin,
  usePutProject,
} from '../../utils/api-client/api-hooks.ts'
import type { ProjectResponse, Repository } from '../../utils/api-client/api-schemas.ts'
import { formatRelativeTime } from '../../utils/date-utils.ts'
import { pluralize } from '../../utils/text-utils.ts'
import * as styles from './project-details-page.css.ts'

/** The members list is bounded by one page; a project this large is a pattern project, not a hand-built one. */
const MEMBERS_PAGE_SIZE = 200

type Overlay =
  | { kind: 'add' }
  | { kind: 'delete' }
  | { kind: 'move'; repository: Repository }
  | { kind: 'remove'; repository: Repository }

export const ProjectDetailsPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>()
  invariant(projectId, 'projectId is required')
  const { data: project, isPending, isError } = useGetProject({ projectId })

  if (isPending) {
    return (
      <div className={layout.page}>
        <p className={layout.loading}>Loading project…</p>
      </div>
    )
  }

  // An error wins over cached data on purpose: after a delete, or a link to a project that is gone,
  // the cache may still hold the last good response, and rendering it would offer actions that 404.
  if (isError || !project) {
    return (
      <div className={layout.page}>
        <Breadcrumb
          navAriaLabel="Project breadcrumb"
          items={[
            { label: 'Projects', to: Routes.ProjectsPage.path },
            { label: 'Not found', to: Routes.ProjectDetailsPage.createPath({ projectId }) },
          ]}
        />
        <div className={layout.emptyCard}>
          <h3 className={layout.emptyTitle}>Project not found</h3>
          <p className={layout.emptyText}>It may have been deleted. The projects list has what remains.</p>
        </div>
      </div>
    )
  }

  return <ProjectDetailsContent key={projectId} project={project} />
}

const ProjectDetailsContent: React.FC<{ project: ProjectResponse }> = ({ project }) => {
  const projectId = project.id
  const navigate = useNavigate()
  const isAdmin = useIsAdmin()
  const { isError: currentUserFailed } = useCurrentUser()
  const [overlay, setOverlay] = useState<Overlay>()
  const [isRenaming, setIsRenaming] = useState(false)
  const renameButtonRef = useRef<HTMLButtonElement>(null)
  const finishRenaming = () => {
    setIsRenaming(false)
    // The field replaced the button, so focus returns to where the edit began.
    requestAnimationFrame(() => renameButtonRef.current?.focus())
  }

  const { data: members } = useGetRepositories({ pageIndex: 0, pageSize: MEMBERS_PAGE_SIZE, projectIds: [projectId] })
  const repositories = useMemo(() => members?._embedded.items ?? [], [members])
  const repositoryCount = members?.total ?? project.repository_count

  const closeOverlay = () => setOverlay(undefined)

  return (
    <div className={layout.page}>
      <Breadcrumb
        navAriaLabel="Project breadcrumb"
        items={[
          { label: 'Projects', to: Routes.ProjectsPage.path },
          { label: project.name, to: Routes.ProjectDetailsPage.createPath({ projectId }) },
        ]}
      />

      <div className={styles.header}>
        <div className={layout.titleBlock}>
          {isRenaming ? (
            <RenameField project={project} onDone={finishRenaming} />
          ) : (
            <>
              <h1 className={layout.heading}>{project.name}</h1>
              <p className={layout.description}>
                {repositoryCount} {pluralize(repositoryCount, 'repository', 'repositories')}
                {project.description && <> · {project.description}</>}
              </p>
            </>
          )}
        </div>
        {isAdmin === true && !isRenaming && (
          <div className={styles.headerActions}>
            <TooltipWithHover
              size="small"
              trigger={
                <button
                  ref={renameButtonRef}
                  type="button"
                  className={styles.headerButton}
                  aria-label="Rename project"
                  onClick={() => setIsRenaming(true)}
                >
                  <PencilSimpleIcon size={20} weight="light" aria-hidden />
                </button>
              }
            >
              Rename project
            </TooltipWithHover>
            <TooltipWithHover
              size="small"
              trigger={
                <button
                  type="button"
                  className={styles.headerButtonDanger}
                  aria-label="Delete project"
                  onClick={() => setOverlay({ kind: 'delete' })}
                >
                  <TrashSimpleIcon size={20} weight="light" aria-hidden />
                </button>
              }
            >
              Delete project
            </TooltipWithHover>
          </div>
        )}
      </div>

      <div className={styles.tabPanel}>
        {isAdmin === true && (
          <div className={layout.barEnd}>
            <DefaultButton size="small" onClick={() => setOverlay({ kind: 'add' })}>
              + Add repositories
            </DefaultButton>
          </div>
        )}
        {/* Same three-state contract the Projects page follows: a known non-admin is told why the
            controls are missing, and so is an admin whose role could not be read. */}
        {isAdmin === false && (
          <p className={layout.readOnlyNotice}>Only administrators can change a project&apos;s repositories.</p>
        )}
        {isAdmin === undefined && currentUserFailed && (
          <p className={layout.readOnlyNotice}>Unable to confirm your permissions. Reload the page to try again.</p>
        )}
        {members && members.total > repositories.length && (
          <p className={layout.readOnlyNotice}>
            Showing the first {repositories.length} of {members.total} repositories.
          </p>
        )}
        {repositories.length > 0 ? (
          <RepositoryCards
            repositories={repositories}
            canManage={isAdmin === true}
            onMove={repository => setOverlay({ kind: 'move', repository })}
            onRemove={repository => setOverlay({ kind: 'remove', repository })}
          />
        ) : (
          <div className={layout.emptyCard}>
            <h3 className={layout.emptyTitle}>No repositories yet</h3>
            <p className={layout.emptyText}>Add repositories to scope feedback to them.</p>
          </div>
        )}
      </div>

      {overlay &&
        match(overlay)
          .with({ kind: 'add' }, () => (
            <AddRepositoriesDialog project={project} onClose={closeOverlay} onAdded={closeOverlay} />
          ))
          .with({ kind: 'delete' }, () => (
            <DeleteProjectDialog
              project={project}
              onClose={closeOverlay}
              onDeleted={() => void navigate(Routes.ProjectsPage.path)}
            />
          ))
          .with({ kind: 'move' }, ({ repository }) => (
            <MoveRepositoryDialog
              repository={repository}
              currentProject={project}
              onClose={closeOverlay}
              onMoved={closeOverlay}
            />
          ))
          .with({ kind: 'remove' }, ({ repository }) => (
            <RemoveRepositoryDialog
              repository={repository}
              project={project}
              onClose={closeOverlay}
              onRemoved={closeOverlay}
            />
          ))
          .exhaustive()}
    </div>
  )
}

const RepositoryCards: React.FC<{
  repositories: Repository[]
  canManage: boolean
  onMove: (repository: Repository) => unknown
  onRemove: (repository: Repository) => unknown
}> = ({ repositories, canManage, onMove, onRemove }) => {
  const riskScores = useRepositoryRiskScores(repositories)
  return (
    <div className={cardStyles.list}>
      {repositories.map(repository => (
        <RepositoryRow
          key={repository.id}
          repository={repository}
          riskScore={riskScores.get(repository.id)?.riskScore}
          onMove={canManage ? () => onMove(repository) : undefined}
          onRemove={canManage ? () => onRemove(repository) : undefined}
        />
      ))}
    </div>
  )
}

const RepositoryRow: React.FC<{
  repository: Repository
  riskScore?: number
  onMove?: () => unknown
  onRemove?: () => unknown
}> = ({ repository, riskScore, onMove, onRemove }) => {
  const { data: latestAnalysis } = useGetLatestAnalysisForRepository({ repositoryId: repository.id })
  const scannedAt = latestAnalysis?._embedded?.scan?.scanned_at ?? latestAnalysis?.current_state.timestamp
  return (
    <ProjectRepositoryCard
      repository={repository}
      riskScore={riskScore}
      lastScanned={scannedAt ? formatRelativeTime(scannedAt) : undefined}
      onMove={onMove}
      onRemove={onRemove}
    />
  )
}

const RenameField: React.FC<{ project: ProjectResponse; onDone: () => unknown }> = ({ project, onDone }) => {
  const { addToast } = useToastService()
  const [value, setValue] = useState(project.name)
  const [conflict, setConflict] = useState<string>()
  const inputRef = useRef<HTMLInputElement>(null)
  const isCommittingRef = useRef(false)

  useEffect(() => {
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [])

  const showError = useShowMutationError({
    prefix: 'Failed to rename project',
    overrides: ADMIN_REQUIRED_OVERRIDE,
  })
  const renameMutation = usePutProject({
    onSuccess: renamed => {
      addToast({ message: <>Project renamed to &ldquo;{renamed.name}&rdquo;</>, variant: 'success' })
      onDone()
    },
    onError: (error, ...rest) => {
      // A taken name is the one failure the typist can fix, so the field stays open with the reason.
      if (
        error.status === 409 &&
        (error.problemType === undefined || error.problemType === DUPLICATE_PROJECT_PROBLEM_TYPE)
      ) {
        isCommittingRef.current = false
        setConflict(`A project named ${value.trim()} already exists.`)
        requestAnimationFrame(() => inputRef.current?.focus())
        return
      }
      showError(error, ...rest)
      onDone()
    },
  })

  const commit = () => {
    if (isCommittingRef.current) {
      return
    }
    isCommittingRef.current = true
    const name = value.trim()
    if (name === '' || name === project.name) {
      onDone()
      return
    }
    renameMutation.mutate({ projectId: project.id, body: { name } })
  }

  return (
    <>
      <input
        ref={inputRef}
        className={styles.renameInput}
        value={value}
        aria-label="Project name"
        aria-invalid={conflict !== undefined}
        aria-describedby={conflict ? 'project-rename-conflict' : undefined}
        onChange={event => {
          setValue(event.target.value)
          setConflict(undefined)
        }}
        onKeyDown={event => {
          if (event.key === 'Enter') {
            event.preventDefault()
            commit()
          }
          // Escape abandons the edit; the field is discarded, so the name is simply never written.
          if (event.key === 'Escape') {
            event.preventDefault()
            isCommittingRef.current = true
            onDone()
          }
        }}
        // Clicking away commits, the way an inline edit is expected to behave — unless the name was just
        // refused, in which case leaving the field abandons the attempt rather than resubmitting it.
        onBlur={() => {
          if (conflict !== undefined) {
            isCommittingRef.current = true
            onDone()
            return
          }
          commit()
        }}
        disabled={renameMutation.isPending}
      />
      {conflict && (
        <p className={styles.renameConflict} id="project-rename-conflict" role="alert">
          {conflict}
        </p>
      )}
      <p className={styles.renameHint}>Enter to save · Escape to cancel</p>
    </>
  )
}
